<script lang="ts">
  import { authUser } from '../lib/firebase';
  import { callAdmin } from '../lib/adminApi';
  import {
    listMembers,
    SAMPLE, STATUSES, ROLES, type Member, type Status, type Role,
  } from '../lib/members';

  let rows = $state<Member[]>([]);
  let loading = $state(true);
  let error = $state('');
  let preview = $state(false);           // showing local sample data (no Firestore writes)

  // Filters
  let q = $state('');
  let fStatus = $state<'all' | Status>('all');
  let fRole = $state<'all' | Role>('all');

  // Seed / sync (Phase 2): backfill members + claims from existing Auth users.
  let syncing = $state(false);
  let syncMsg = $state('');
  async function syncUsers() {
    syncing = true; syncMsg = '';
    try {
      const r = await callAdmin<{ total: number; created: number; updated: number }>('seed');
      syncMsg = `Synced ${r.total} users — ${r.created} added, ${r.updated} updated.`;
      await load();
    } catch (e) {
      syncMsg = `Sync failed: ${(e as Error)?.message ?? e}`;
    } finally { syncing = false; }
  }

  // Add / invite form
  let addOpen = $state(false);
  let addEmail = $state('');
  let addRole = $state<Role>('patient');
  let addOrg = $state('');

  // Last activity per email, from Firebase Auth via the backend (the registry
  // doesn't store logins). Stays hidden until the lastLogins op responds.
  let logins = $state<Record<string, number>>({});
  let loginsLoaded = $state(false);
  async function loadLogins() {
    try {
      const r = await callAdmin<{ logins: Record<string, number> }>('lastLogins');
      logins = r.logins || {}; loginsLoaded = true;
    } catch { logins = {}; loginsLoaded = false; }
  }

  // Per-record grants — the OTHER kind of access. The Role column is the
  // ACCOUNT role; someone can be a `patient` here yet hold provider access to a
  // specific record, which is what these badges surface (and what Remove revokes).
  type Grant = { guardian: number; provider: number };
  let grants = $state<Record<string, Grant>>({});
  async function loadGrants() {
    try {
      const r = await callAdmin<{ grants: Record<string, Grant> }>('shareGrants');
      grants = r.grants || {};
    } catch { grants = {}; }
  }
  function grantLabel(email: string): string {
    const g = grants[email]; if (!g) return '';
    const bits: string[] = [];
    if (g.provider) bits.push(`provider on ${g.provider} record${g.provider === 1 ? '' : 's'}`);
    if (g.guardian) bits.push(`guardian on ${g.guardian} record${g.guardian === 1 ? '' : 's'}`);
    return bits.join(' · ');
  }
  const grantTotal = (email: string) => { const g = grants[email]; return g ? g.guardian + g.provider : 0; };
  function fmtWhen(ms: number): string {
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(ms).toLocaleDateString();
  }

  async function load() {
    loading = true; error = ''; preview = false;
    loadLogins(); loadGrants();
    try { rows = await listMembers(); }
    catch (e) { error = (e as Error)?.message || String(e); rows = []; }
    finally { loading = false; }
  }
  function loadSample() {
    rows = SAMPLE.map((m) => ({ ...m })); preview = true; error = ''; loading = false;
    logins = {
      'vishnu@dreamgiga.ai': Date.now() - 20 * 60e3,
      'eashwer.family@gmail.com': Date.now() - 26 * 3600e3,
      'dr.jsharma@inova.org': Date.now() - 9 * 86400e3,
      'dr.harik@texaschildrens.org': Date.now() - 3 * 86400e3,
    };
    loginsLoaded = true;
  }

  $effect(() => { load(); });

  const filtered = $derived(
    rows.filter((m) => {
      if (fStatus !== 'all' && m.status !== fStatus) return false;
      if (fRole !== 'all' && m.role !== fRole) return false;
      const s = q.trim().toLowerCase();
      if (s && !`${m.email} ${m.displayName ?? ''} ${m.org ?? ''}`.toLowerCase().includes(s)) return false;
      return true;
    }),
  );
  const counts = $derived({
    total: rows.length,
    pending: rows.filter((m) => m.status === 'pending').length,
    active: rows.filter((m) => m.status === 'active').length,
    invited: rows.filter((m) => m.status === 'invited').length,
    blocked: rows.filter((m) => m.status === 'blocked').length,
    physicians: rows.filter((m) => m.role === 'physician').length,
  });

  function patch(email: string, fields: Partial<Member>) {
    rows = rows.map((m) => (m.email === email ? { ...m, ...fields } : m));
  }
  async function guard(make: () => Promise<unknown>, undo: () => void) {
    if (preview) return;   // preview = local only; never touch the backend
    try { await make(); } catch (e) { alert(`Could not save: ${(e as Error)?.message ?? e}`); undo(); }
  }

  // Approve a pending request (or re-issue for an invited member): the backend
  // flips status → invited and returns the one-time 16-hex activation code, which
  // is shown ONCE here — only its hash is stored anywhere.
  let issued = $state<{ email: string; code: string } | null>(null);
  let copied = $state(false);
  async function approveWithCode(m: Member) {
    if (preview) { patch(m.email, { status: 'invited' }); issued = { email: m.email, code: 'a1b2c3d4e5f60718' }; return; }
    try {
      const r = await callAdmin<{ code: string; emailed: boolean }>('approve', { email: m.email });
      patch(m.email, { status: 'invited' });
      issued = { email: m.email, code: r.code }; copied = false;
    } catch (e) { alert(`Could not approve: ${(e as Error)?.message ?? e}`); }
  }
  async function copyCode() {
    if (!issued) return;
    try { await navigator.clipboard.writeText(issued.code); copied = true; } catch { /* clipboard blocked — copy manually */ }
  }
  const fmtCode = (c: string) => c.replace(/(.{4})(?=.)/g, '$1 ');

  // Mutations route through the admin backend so custom claims (approved/role/blocked)
  // stay in sync with the registry. In preview mode `guard` no-ops (local only).
  function changeStatus(m: Member, status: Status) {
    const prev = m.status; patch(m.email, { status });
    guard(() => callAdmin('setStatus', { email: m.email, status }), () => patch(m.email, { status: prev }));
  }
  function changeRole(m: Member, role: Role) {
    const prev = m.role; patch(m.email, { role });
    guard(() => callAdmin('setRole', { email: m.email, role }), () => patch(m.email, { role: prev }));
  }
  function remove(m: Member) {
    const n = grantTotal(m.email);
    const extra = n
      ? `\n\nThis also revokes their access to ${n} shared record${n === 1 ? '' : 's'} (${grantLabel(m.email)}) — the owner would have to re-share to undo it.`
      : '';
    if (!confirm(`Remove ${m.email} from the registry? Their access is revoked immediately and they cannot request it again until you re-invite them.${extra}`)) return;
    const prev = rows; rows = rows.filter((x) => x.email !== m.email);
    guard(async () => {
      const r = await callAdmin<{ sharesRevoked: number }>('remove', { email: m.email });
      grants = { ...grants, [m.email]: { guardian: 0, provider: 0 } };
      syncMsg = `Removed ${m.email}${r.sharesRevoked ? ` — revoked ${r.sharesRevoked} shared record${r.sharesRevoked === 1 ? '' : 's'}` : ''}.`;
    }, () => (rows = prev));
  }
  async function addMember() {
    const email = addEmail.trim().toLowerCase();
    const org = addOrg.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { alert('Enter a valid email.'); return; }
    if (rows.some((m) => m.email === email)) { alert('That email is already in the registry.'); return; }
    const m: Member = { email, status: 'invited', role: addRole, org: org || undefined, invitedBy: $authUser?.email ?? '' };
    rows = [...rows, m].sort((a, b) => a.email.localeCompare(b.email));
    addEmail = ''; addOrg = ''; addOpen = false;
    guard(async () => {
      const r = await callAdmin<{ code: string; emailed: boolean }>('invite', { email, role: addRole, org: org || undefined });
      issued = { email, code: r.code }; copied = false;
    }, () => (rows = rows.filter((x) => x.email !== email)));
  }
</script>

<div class="body">
    <div class="head">
      <div>
        <h1>User directory &amp; access</h1>
        <p class="muted">Who may sign in, their role, and their status. This is the registry the login gate reads.</p>
      </div>
      <div class="headacts">
        <button class="sync" onclick={syncUsers} disabled={syncing} title="Backfill the registry + claims from existing accounts">
          {syncing ? 'Syncing…' : '⟳ Sync existing users'}
        </button>
        <button class="add" onclick={() => (addOpen = !addOpen)}>{addOpen ? 'Close' : '+ Add / invite'}</button>
      </div>
    </div>

    {#if syncMsg}<div class="banner {syncMsg.startsWith('Sync failed') ? 'err' : 'warn'}">{syncMsg}</div>{/if}

    {#if issued}
      <div class="banner codebanner">
        <strong>Activation code for {issued.email}</strong>
        <span class="bigcode mono">{fmtCode(issued.code)}</span>
        <div class="bactions">
          <button onclick={copyCode}>{copied ? 'Copied ✓' : 'Copy code'}</button>
          <button class="ghost" onclick={() => (issued = null)}>Dismiss</button>
        </div>
        <span class="hint">Shown once — share it with them out-of-band (only its hash is stored). They enter it in the app to activate. "New code" invalidates this one.</span>
      </div>
    {/if}

    {#if preview}
      <div class="banner warn">
        <strong>Preview data.</strong> The enforcement rules aren't deployed yet, so this shows sample rows — nothing is written to Firestore. Actions here only change the on-screen preview.
      </div>
    {:else if error}
      <div class="banner err">
        <strong>Can't read the registry yet.</strong> {error}
        <div class="bactions">
          <button onclick={load}>Retry</button>
          <button class="ghost" onclick={loadSample}>Preview with sample data</button>
        </div>
        <span class="hint">Expected before deploy — the <code>members</code> rules aren't live, so this account can't read it yet.</span>
      </div>
    {/if}

    {#if addOpen}
      <div class="addform">
        <input class="in" type="email" placeholder="email@example.com" bind:value={addEmail} />
        <select class="in sel" bind:value={addRole}>
          {#each ROLES as r}<option value={r}>{r}</option>{/each}
        </select>
        <input class="in" type="text" placeholder="Organization (physicians)" bind:value={addOrg} />
        <button class="primary" onclick={addMember}>Send invite</button>
        <span class="hint">Adds the registry entry (status <em>invited</em>), generates their one-time activation code (shown to you once), and emails a sign-in link via Resend. They activate by signing in and entering the code.</span>
      </div>
    {/if}

    <div class="stats">
      <span class="stat"><b class="num">{counts.total}</b> total</span>
      {#if counts.pending}<span class="stat warn"><b class="num">{counts.pending}</b> pending approval</span>{/if}
      <span class="stat ok"><b class="num">{counts.active}</b> active</span>
      <span class="stat warn"><b class="num">{counts.invited}</b> invited</span>
      <span class="stat danger"><b class="num">{counts.blocked}</b> blocked</span>
      <span class="stat"><b class="num">{counts.physicians}</b> physicians</span>
    </div>

    <div class="filters">
      <input class="in search" type="search" placeholder="Search email, name, org…" bind:value={q} />
      <select class="in sel" bind:value={fStatus}>
        <option value="all">All statuses</option>
        {#each STATUSES as s}<option value={s}>{s}</option>{/each}
      </select>
      <select class="in sel" bind:value={fRole}>
        <option value="all">All roles</option>
        {#each ROLES as r}<option value={r}>{r}</option>{/each}
      </select>
    </div>

    <div class="tablewrap">
      <table>
        <thead>
          <tr><th>Member</th><th>Role</th><th>Status</th><th class="ta-r">Actions</th></tr>
        </thead>
        <tbody>
          {#if loading}
            <tr><td colspan="4" class="empty">Loading…</td></tr>
          {:else if filtered.length === 0}
            <tr><td colspan="4" class="empty">{rows.length ? 'No members match the filters.' : 'No members yet.'}</td></tr>
          {:else}
            {#each filtered as m (m.email)}
              <tr>
                <td>
                  <div class="who">
                    <span class="name">{m.displayName || '—'}</span>
                    {#if loginsLoaded}
                      {#if logins[m.email]}
                        <span class="lastlogin" title={new Date(logins[m.email]).toLocaleString()}>Last active {fmtWhen(logins[m.email])}</span>
                      {:else}
                        <span class="lastlogin">Never signed in</span>
                      {/if}
                    {/if}
                    <span class="mono email2">{m.email}</span>
                    {#if grantLabel(m.email)}<span class="grant" title="Per-record sharing, separate from the account role. Removing this member revokes it.">🔑 {grantLabel(m.email)}</span>{/if}
                    {#if m.org}<span class="org">{m.org}</span>{/if}
                  </div>
                </td>
                <td>
                  <select class="in sel role" value={m.role} onchange={(e) => changeRole(m, (e.currentTarget as HTMLSelectElement).value as Role)}>
                    {#each ROLES as r}<option value={r}>{r}</option>{/each}
                  </select>
                </td>
                <td><span class="pill {m.status}">{m.status}</span></td>
                <td class="ta-r">
                  <div class="rowacts">
                    {#if m.status === 'pending'}
                      <button class="act ok" onclick={() => approveWithCode(m)} title="Approve and issue a one-time activation code">Approve</button>
                    {:else if m.status === 'invited'}
                      <button class="act ok" onclick={() => approveWithCode(m)} title="Issue a fresh activation code (invalidates the old one)">New code</button>
                      <button class="act" onclick={() => changeStatus(m, 'active')} title="Activate directly, skipping the code">Activate</button>
                    {:else if m.status === 'blocked'}
                      <button class="act ok" onclick={() => changeStatus(m, 'active')}>Unblock</button>
                    {/if}
                    {#if m.status !== 'blocked'}
                      <button class="act danger" onclick={() => changeStatus(m, 'blocked')}>Block</button>
                    {/if}
                    <button class="act ghost" onclick={() => remove(m)} title="Remove from registry" aria-label="Remove">✕</button>
                  </div>
                </td>
              </tr>
            {/each}
          {/if}
        </tbody>
      </table>
    </div>

    <p class="footnote">
      Account roles — <b>patient</b> · <b>physician</b> · <b>admin</b> — say what someone is on their OWN account. They are separate from
      per-record sharing: anyone can be made a guardian or provider on another person's record from the patient app, which is what a
      🔑 badge shows. Block = revoked on next token refresh (reversible, keeps their shares); Remove = permanent, revokes their shares too.
    </p>
</div>

<style>
  .body { width: 100%; padding: var(--space-6) var(--space-8); display: flex; flex-direction: column; gap: var(--space-4); }
  .head { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  h1 { font-weight: var(--fw-heading); letter-spacing: -0.01em; }
  .muted { color: var(--text-3); font-size: var(--fs-body); margin-top: 2px; }
  .add { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--accent-soft); color: var(--accent); border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent); font-weight: 700; font-size: var(--fs-xs); }
  .add:hover { background: color-mix(in srgb, var(--accent) 15%, transparent); }
  .headacts { display: inline-flex; gap: var(--space-2); flex-wrap: wrap; }
  .sync { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); font-weight: 700; font-size: var(--fs-xs); }
  .sync:hover:not(:disabled) { color: var(--accent); border-color: color-mix(in srgb, var(--accent) 40%, transparent); }
  .sync:disabled { opacity: 0.6; cursor: default; }

  .banner { padding: var(--space-3) var(--space-4); border-radius: var(--radius-md); font-size: var(--fs-xs); line-height: 1.5; }
  .banner.warn { color: var(--warn); background: var(--warn-soft); border: 1px solid color-mix(in srgb, var(--warn) 34%, transparent); }
  .banner.err { color: var(--text-2); background: var(--surface); border: 1px solid var(--line-strong); }
  .banner .bactions { display: flex; gap: var(--space-2); margin-top: var(--space-2); }
  .banner .bactions button { font-size: var(--fs-2xs); font-weight: 700; padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); background: var(--accent); color: var(--on-accent); }
  .banner .bactions .ghost { background: var(--surface-2); color: var(--text-2); border: 1px solid var(--line-strong); }
  .banner .hint { display: block; margin-top: var(--space-2); color: var(--text-3); font-size: var(--fs-2xs); }
  code { font-family: var(--mono); font-size: 0.92em; color: var(--accent); background: var(--accent-soft); padding: 1px 5px; border-radius: 5px; }

  .addform { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); padding: var(--space-3); background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); }
  .addform .hint { width: 100%; color: var(--text-3); font-size: var(--fs-2xs); }
  .in { background: var(--surface-2); border: 1px solid var(--line-strong); border-radius: var(--radius-sm); padding: var(--space-2) var(--space-3); color: var(--text); font-size: var(--fs-sm); }
  .in:focus { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 28%, transparent); }
  .sel { cursor: pointer; }
  .primary { padding: var(--space-2) var(--space-4); border-radius: var(--radius-sm); background: var(--accent-grad); color: var(--on-accent); font-weight: 700; font-size: var(--fs-xs); }

  .stats { display: flex; flex-wrap: wrap; gap: var(--space-2) var(--space-4); }
  .stat { font-size: var(--fs-xs); color: var(--text-3); }
  .stat b { color: var(--text); margin-right: 4px; font-size: var(--fs-md); }
  .stat.ok b { color: var(--ok); } .stat.warn b { color: var(--warn); } .stat.danger b { color: var(--danger); }

  .filters { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  .search { flex: 1; min-width: 200px; }

  .tablewrap { border: 1px solid var(--line); border-radius: var(--radius-md); overflow-x: auto; background: var(--surface); }
  table { width: 100%; border-collapse: collapse; min-width: 640px; }
  th, td { text-align: left; padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--line); vertical-align: middle; }
  thead th { font-size: var(--fs-2xs); letter-spacing: 0.12em; text-transform: uppercase; color: var(--text-3); background: var(--surface-2); }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover { background: color-mix(in srgb, var(--accent) 7%, transparent); }
  .ta-r { text-align: right; }
  .empty { text-align: center; color: var(--text-3); padding: var(--space-8); }

  .who { display: flex; flex-direction: column; gap: 2px; }
  .name { font-weight: 600; }
  .mono { font-family: var(--mono); }
  .email2 { font-size: var(--fs-xs); color: var(--text-3); }
  .lastlogin { font-size: var(--fs-2xs); color: var(--text-3); }
  .grant { font-size: var(--fs-2xs); color: var(--warn); }
  .org { font-size: var(--fs-2xs); color: var(--accent); }

  .role { padding: 4px 8px; font-size: var(--fs-xs); }

  .pill { display: inline-block; font-size: var(--fs-2xs); font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 10px; border-radius: var(--radius-pill); }
  .pill.active { color: var(--ok); background: var(--ok-soft); border: 1px solid color-mix(in srgb, var(--ok) 34%, transparent); }
  .pill.invited { color: var(--warn); background: var(--warn-soft); border: 1px solid color-mix(in srgb, var(--warn) 34%, transparent); }
  .pill.pending { color: var(--accent); background: var(--accent-soft); border: 1px solid color-mix(in srgb, var(--accent) 34%, transparent); }
  .codebanner { display: flex; flex-direction: column; gap: var(--space-2); color: var(--text); background: var(--accent-soft); border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent); }
  .bigcode { font-size: var(--fs-lg, 1.15rem); font-weight: 800; letter-spacing: 0.14em; color: var(--accent); user-select: all; }
  .pill.blocked { color: var(--danger); background: var(--danger-soft); border: 1px solid color-mix(in srgb, var(--danger) 34%, transparent); }

  .rowacts { display: inline-flex; gap: var(--space-2); justify-content: flex-end; }
  .act { font-size: var(--fs-2xs); font-weight: 700; padding: var(--space-1) var(--space-3); border-radius: var(--radius-pill); border: 1px solid var(--line-strong); color: var(--text-2); background: var(--surface-2); }
  .act.ok:hover { color: var(--ok); border-color: var(--ok); }
  .act.danger:hover { color: var(--danger); border-color: var(--danger); }
  .act.ghost { color: var(--text-3); }
  .act.ghost:hover { color: var(--danger); border-color: var(--danger); }

  .footnote { color: var(--text-3); font-size: var(--fs-2xs); line-height: 1.6; }
  .footnote b { color: var(--text-2); }
</style>
